import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Save, X, ExternalLink, Image as ImgIcon } from "lucide-react";
import SectionLabel from "@/components/brand/SectionLabel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blank = {
  title: "",
  tag: "MARKETING SITE",
  year: String(new Date().getFullYear()),
  img: "",
  url: "",
  is_logo: false,
  bg: "",
  order: 0,
};

export default function DesignProjectsAdmin() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // project id or "new"
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/designs/projects`, {
        withCredentials: true,
      });
      setProjects(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Couldn't load design projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const startNew = () => {
    setEditing("new");
    setForm({ ...blank, order: projects.length });
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      title: p.title || "",
      tag: p.tag || "MARKETING SITE",
      year: p.year || "",
      img: p.img || "",
      url: p.url || "",
      is_logo: !!p.is_logo,
      bg: p.bg || "",
      order: typeof p.order === "number" ? p.order : 0,
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(blank);
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/designs/projects`, form, {
          withCredentials: true,
        });
        toast.success("Project created.");
      } else {
        await axios.put(`${API}/admin/designs/projects/${editing}`, form, {
          withCredentials: true,
        });
        toast.success("Project updated.");
      }
      cancelEdit();
      fetchAll();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/designs/projects/${p.id}`, {
        withCredentials: true,
      });
      toast.success("Project deleted.");
      fetchAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <section className="mb-12" data-testid="admin-design-projects">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <SectionLabel className="mb-3">Epicom Designs</SectionLabel>
          <h2 className="heading text-2xl md:text-3xl text-white uppercase">
            Design Portfolio
          </h2>
          <p className="text-steel-light text-sm mt-2 max-w-2xl">
            Projects shown on the <code className="text-signal">/designs</code> page.
          </p>
        </div>
        {editing === null && (
          <button
            onClick={startNew}
            data-testid="admin-new-design-btn"
            className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider px-5 py-3 transition-colors"
          >
            <Plus size={16} /> New Project
          </button>
        )}
      </div>

      {/* Form */}
      {editing !== null && (
        <form
          onSubmit={save}
          data-testid="admin-design-form"
          className="border border-signal/60 bg-bg-secondary p-6 md:p-8 mb-8 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="heading text-xl text-white uppercase">
              {editing === "new" ? "New Project" : "Edit Project"}
            </h3>
            <button
              type="button"
              onClick={cancelEdit}
              className="text-steel-light hover:text-signal transition-colors"
              aria-label="Cancel"
            >
              <X size={20} />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <F label="Title *">
              <input
                type="text"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="admin-design-title"
                className="adm-input"
                placeholder="e.g. Rebel Angels"
              />
            </F>
            <F label="Tag">
              <input
                type="text"
                value={form.tag}
                onChange={(e) => setForm({ ...form, tag: e.target.value })}
                data-testid="admin-design-tag"
                className="adm-input"
                placeholder="MARKETING SITE · E-COMMERCE · DASHBOARD"
              />
            </F>
            <F label="Year">
              <input
                type="text"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: e.target.value })}
                data-testid="admin-design-year"
                className="adm-input"
                placeholder="2026"
              />
            </F>
            <F label="Order (lower = first)">
              <input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })
                }
                data-testid="admin-design-order"
                className="adm-input"
              />
            </F>
          </div>

          <F label="Image URL or path (leave blank to auto-thumbnail the target URL)">
            <input
              type="text"
              value={form.img}
              onChange={(e) => setForm({ ...form, img: e.target.value })}
              data-testid="admin-design-img"
              className="adm-input"
              placeholder="/brand/your-project.jpg  or  https://…  (blank = live screenshot)"
            />
          </F>

          <F label="External URL (optional — makes the card clickable)">
            <input
              type="url"
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              data-testid="admin-design-url"
              className="adm-input"
              placeholder="https://example.com"
            />
          </F>

          <div className="grid sm:grid-cols-2 gap-5">
            <label className="inline-flex items-center gap-3 cursor-pointer pt-7">
              <input
                type="checkbox"
                checked={!!form.is_logo}
                onChange={(e) =>
                  setForm({ ...form, is_logo: e.target.checked })
                }
                data-testid="admin-design-is-logo"
                className="accent-signal w-4 h-4"
              />
              <span className="font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
                Render as logo on solid background
              </span>
            </label>
            {form.is_logo && (
              <F label="Background color (hex)">
                <input
                  type="text"
                  value={form.bg}
                  onChange={(e) => setForm({ ...form, bg: e.target.value })}
                  data-testid="admin-design-bg"
                  className="adm-input"
                  placeholder="#0a0a0a"
                />
              </F>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={cancelEdit}
              className="font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal px-5 py-3 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="admin-design-save"
              className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover disabled:opacity-50 text-white font-bold uppercase tracking-wider px-6 py-3 transition-colors"
            >
              <Save size={14} />
              {saving ? "Saving…" : editing === "new" ? "Create Project" : "Save Changes"}
            </button>
          </div>
        </form>
      )}

      {/* List */}
      {loading ? (
        <div className="font-mono text-xs tracking-[0.25em] text-steel-light uppercase py-12 text-center">
          Loading projects…
        </div>
      ) : projects.length === 0 ? (
        <div className="border border-steel-dark bg-bg-secondary p-10 text-center">
          <p className="text-steel-light mb-5">No projects yet.</p>
          <button
            onClick={startNew}
            className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider px-5 py-3 transition-colors"
          >
            <Plus size={16} /> Add your first project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-steel-dark border border-steel-dark">
          {projects.map((p) => (
            <article
              key={p.id}
              data-testid={`admin-design-row-${p.id}`}
              className="bg-bg-secondary flex flex-col"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-bg-primary">
                {p.img ? (
                  <img
                    src={p.img}
                    alt={p.title}
                    className={`absolute inset-0 w-full h-full ${
                      p.is_logo ? "object-contain p-6" : "object-cover"
                    }`}
                    style={p.is_logo && p.bg ? { background: p.bg } : undefined}
                  />
                ) : p.url ? (
                  // No image — live thumbnail via microlink (matches public Designs page behavior)
                  <img
                    src={`https://api.microlink.io/?url=${encodeURIComponent(
                      p.url,
                    )}&screenshot=true&meta=false&embed=screenshot.url&viewport.width=1440&viewport.height=900&waitFor=1500`}
                    alt={p.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-steel-light">
                    <ImgIcon size={32} />
                  </div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase mb-2">
                  {p.tag} {p.year && `· ${p.year}`}
                </div>
                <h3 className="heading text-lg text-white uppercase mb-2 leading-tight">
                  {p.title}
                </h3>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs text-steel-light hover:text-signal font-mono mb-3 truncate"
                  >
                    <ExternalLink size={11} />
                    <span className="truncate">{p.url}</span>
                  </a>
                )}
                <div className="mt-auto flex items-center gap-2 pt-3 border-t border-steel-dark">
                  <button
                    onClick={() => startEdit(p)}
                    data-testid={`admin-design-edit-${p.id}`}
                    className="inline-flex items-center gap-1.5 p-2 text-steel-light hover:text-signal transition-colors font-mono text-[10px] tracking-[0.2em] uppercase"
                    aria-label="Edit"
                  >
                    <Edit3 size={14} /> Edit
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`admin-design-delete-${p.id}`}
                    className="inline-flex items-center gap-1.5 p-2 text-steel-light hover:text-red-400 transition-colors font-mono text-[10px] tracking-[0.2em] uppercase"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                  <span className="ml-auto font-mono text-[10px] tracking-[0.2em] text-steel-light uppercase">
                    #{p.order}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function F({ label, children }) {
  return (
    <label className="block">
      <span className="block mb-2 font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
        {label}
      </span>
      {children}
    </label>
  );
}
